/* DataExplorerGridViewRenderer.js */

"use strict";
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");
const DistinctValuesFeed = require("../../../../lib/DistinctValuesFeed");

/**
 * @listens DataExplorerGridViewRenderer#event:PathsEditingState
 */
class DataExplorerGridViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("data-explorer-view-gl");
        this._logger = this._model.logger;

        this._setGroupTitles = _.debounce(this._setGroupTitles.bind(this), 50);
        this._setGrouping = _.debounce(this._setGrouping.bind(this), 300);
        this.appendGrid = _.debounce(this.appendGrid.bind(this), 700);
        this._groupingValuesFeed = new DistinctValuesFeed({
            startFeed: (fieldName) => {
                const distinctQuery = {$userQuery: {}};
                return this.model._explorerTemplate.startAscendExplorerTemplateDistinctValuesFeed(
                    this.model.activeOrgId,
                    fieldName,
                    distinctQuery,
                    "false"
                );
            },
        });
    }

    get selectedRows() {
        return this._explorerTemplateGrid.getSelectedRows();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        //TODO: Just for test. To be removed
        // this._rootElem.addEventListener("click", this._onLabelClick = this._onLabelClick.bind(this) );
        this._explorerTemplateGrid.on(
            "UserSelectedRows",
            (this._onGridUserSelectedRows = this._onGridUserSelectedRows.bind(this))
        );
        this._explorerTemplateGrid.on(
            "UserRowDoubleClicked",
            (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this))
        );
        this._explorerTemplateGrid.on(
            "UserSorted",
            (this._onUserSorted = this._onUserSorted.bind(this))
        );
        this._explorerTemplateGrid.on(
            "GridBusyStart",
            (this._onGridBusyStart = this._onGridBusyStart.bind(this))
        );
        this._explorerTemplateGrid.on(
            "ColumnSettingChange",
            (this._onColumnSettingsChange = this._onColumnSettingsChange.bind(this))
        );

        this._groupingValuesFeed.on("ValueUpdated", this._setGrouping);
        this._model.on("ResetTitles", (this._onResetTitles = this._onResetTitles.bind(this)));

        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._explorerTemplateGrid) {
            this._explorerTemplateGrid.removeAllListeners("UserSelectedRows");
            this._explorerTemplateGrid.removeAllListeners("UserRowDoubleClicked");
            this._explorerTemplateGrid.removeAllListeners("Entities");
            this._explorerTemplateGrid.removeListener("UserSorted", this._onUserSorted);
            this._explorerTemplateGrid.removeListener("GridBusyStart", this._onGridBusyStart);
            this._explorerTemplateGrid.removeListener(
                "ColumnSettingChange",
                this._onColumnSettingsChange
            );
        }
        if (this._groupingValuesFeed) {
            this._groupingValuesFeed.removeListener("ValueUpdated", this._setGrouping);
        }
        if (this._model) {
            this._model.removeListener("ResetTitles", this._onResetTitles);
            if (this._model.pagingFeed) {
                this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
            }
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [
            _.isUndefined(this._explorerTemplateGrid)
                ? Promise.resolve()
                : this._explorerTemplateGrid.shutdown(),
            _.isUndefined(this._groupingValuesFeed)
                ? Promise.resolve()
                : this._groupingValuesFeed.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._logger = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (error) => {
                this._logger.warn({error}, "DataExplorerGridViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        const logger = this._logger;
        return this._model
            .whenModelIsReady()
            .then(() => {
                this.renderGrid();
                this._registerListeners();
                this._onFeedRefreshView(); // On load, show default message as if a refresh was done
                return Promise.resolve();
            })
            .catch((error) => {
                logger.warn({error}, "DataExplorerGridViewRenderer.render() error");
            });
    }

    _setGrouping(...args) {
        if (args.length === 3) {
            // distinctvalue feed gives 3 arguments
            const feed = args[2];
            if (feed && feed._valuesList) {
                // if feed and required props exist
                // since private is true/false. Change in values with a value of 1 for either false or true
                // is a probable case of newly created entity and groupby is called
                // additional cases where this fires but not required is when moving from a larger number to 1.
                // A flicker happens when gropby for these cases as re-render occurs.
                if (!(feed._valuesList.length === 2 && args[1] === 1)) {
                    return;
                }
            }
        }
        // To apply group By
        this._explorerTemplateGrid.setGroupBy("private", "ALL");

        // To remove group By
        // this._explorerTemplateGrid.setGroupBy();
    }

    _setGroupTitles(attempts = 5) {
        // Fields can occasionally error out before _explorerTemplateGrid has a _renderer
        const titles = _.get(this._explorerTemplateGrid, "_renderer._rootElem", false)
            ? this._explorerTemplateGrid._renderer._rootElem.querySelectorAll(".group-field .title")
            : [];
        if (titles.length > 0) {
            titles.forEach((title) => {
                if (title.innerText === "False") {
                    title.classList.add("show");
                    title.innerText = "Shared Templates";
                } else if (title.innerText === "True") {
                    title.classList.add("show");
                    title.innerText = "Private Templates";
                }
            });
        } else if (attempts > 0) {
            setTimeout(() => this._setGroupTitles(attempts - 1), 50);
        }
    }

    renderGrid() {
        this._explorerTemplateGrid = new EntityGrid({
            gridKey: this.model._gridKey,
            logger: this._logger,
            userGridData: this._model._userGridData,
            columns: this._model.columnConfig,
            pagingFeed: this._model.pagingFeed,
            showHeader: true,
            showLinesNumber: this._model._gameEditor.renderer._rootElem.querySelector(
                "game-editor-view-actions-gl left-container"
            ),
            columnSettingsWidgetContainer:
                this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
            filterComponent:
                this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
            advancedFilterComponent:
                this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
            modalUserFeedback: this._model._modalUserFeedback,
            groupingValuesFeed: this._groupingValuesFeed,
        });
        this._setGrouping();

        this.appendGrid();
    }

    appendGrid() {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._explorerTemplateGrid.renderer.rootElement);
        }
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _onGridUserSelectedRows(selectedRowIds) {
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
        // this._renderAvailableActions(selectedRowIds.selectedRows);
    }

    _onUserDblClickedRow(obj) {
        this._model.openDataExplorerInstanceEditor(
            obj.selectedRow,
            Registrations.DEIE_VIEW_DETAILS
        );
    }

    _onGridBusyStart() {
        if (this._model.pagingFeed.totalEntities > this._model.pagingFeed.pageSize * 2) {
            let gridBusyEnded = false;
            this._explorerTemplateGrid.once("GridBusyEnd", () => {
                gridBusyEnded = true;
            });
            this._model._modalUserFeedback.displayLoading().then(() => {
                if (gridBusyEnded) {
                    this._model._modalUserFeedback.hideDisplayed();
                } else {
                    this._explorerTemplateGrid.once("GridBusyEnd", () => {
                        this._model._modalUserFeedback.hideDisplayed();
                        this._setGroupTitles();
                    });
                }
            });
        }
    }

    _onUserSorted(eventData) {
        this._model.onUserSorted(eventData);
    }

    _onColumnSettingsChange() {
        this._setGroupTitles();
    }

    _onResetTitles() {
        this._setGroupTitles();
    }

    _onFeedRefreshView() {
        //we only show the default msg when there are no groups to display
        const totalGroups = this._groupingValuesFeed._valuesList.length;

        if (totalGroups === 0) {
            if (!this._defaultViewActive) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }

        this._setGroupTitles();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _showDefaultMessageDisplay() {
        let message = {
            message: "Create a new template",
            icon: "dataexplorer.add",
            action: Registrations.DATA_EXPLORER_ACTIONS_CREATE,
        };
        return this._model.gameEditor
            .showDefaultMessageView(message)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceListViewerRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceListViewerRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = DataExplorerGridViewRenderer;
